/**
 * Business Info Utilities
 * Helper functions for normalizing and extracting business information
 */

/**
 * Normalize phone numbers from business info or company fallback
 * @param {Object} businessInfo - Business info from API
 * @param {Object} company - Company fallback data
 * @returns {Array} Array of phone number objects with number and label
 */
export const normalizePhoneNumbers = (businessInfo = {}, company = {}) => {
  const phones = [];

  // Primary phone
  const primaryPhone = businessInfo.phone || company.phone;
  if (primaryPhone) {
    phones.push({
      number: primaryPhone,
      label: 'Main',
    });
  }

  // Alternate phones from business info
  if (businessInfo.alternate_phones) {
    let altPhones = businessInfo.alternate_phones;

    // Parse if string
    if (typeof altPhones === 'string') {
      try {
        altPhones = JSON.parse(altPhones);
      } catch (e) {
        altPhones = [];
      }
    }

    // Handle array format
    if (Array.isArray(altPhones)) {
      altPhones.forEach((phone, index) => {
        if (typeof phone === 'string' && phone.trim()) {
          phones.push({
            number: phone.trim(),
            label: `Alt ${index + 1}`,
          });
        } else if (phone && phone.number) {
          phones.push({
            number: phone.number,
            label: phone.label || `Alt ${index + 1}`,
          });
        }
      });
    }
  }

  // Individual alt phone fields (phone_alt_1, phone_alt_2)
  if (businessInfo.phone_alt_1) {
    phones.push({
      number: businessInfo.phone_alt_1,
      label: 'Alt 1',
    });
  }
  if (businessInfo.phone_alt_2) {
    phones.push({
      number: businessInfo.phone_alt_2,
      label: 'Alt 2',
    });
  }

  return phones;
};

/**
 * Get address lines from business info or company fallback
 * @param {Object} businessInfo - Business info from API
 * @param {Object} company - Company fallback data
 * @returns {Object} Address object with street, suburb, state, postcode, full
 */
export const getAddressLines = (businessInfo = {}, company = {}) => {
  const address = {
    street: businessInfo.street_address || businessInfo.address || company.address || '',
    suburb: businessInfo.suburb || company.suburb || '',
    state: businessInfo.state || company.state || 'VIC',
    postcode: businessInfo.postcode || company.postcode || '',
  };

  // Build full address
  const parts = [address.street];
  if (address.suburb) {
    parts.push(`${address.suburb}${address.state ? ` ${address.state}` : ''}${address.postcode ? ` ${address.postcode}` : ''}`);
  }
  address.full = parts.filter(Boolean).join(', ');

  return address;
};

/**
 * Get contact email from business info or company fallback
 * @param {Object} businessInfo - Business info from API
 * @param {Object} company - Company fallback data
 * @returns {string} Email address
 */
export const getContactEmail = (businessInfo = {}, company = {}) => {
  return businessInfo.email || company.email || '';
};

/**
 * Get accreditation info from business info and site settings
 * @param {Object} businessInfo - Business info from API
 * @param {Object} siteSettings - Site settings from API
 * @returns {Object} Accreditation info with vacc_member, logo, link, text
 */
export const getAccreditationInfo = (businessInfo = {}, siteSettings = {}) => {
  const isVaccMember = businessInfo.vacc_member || siteSettings.vacc_member || false;

  // Get VACC logo URL - check multiple possible sources
  const vaccLogo = businessInfo.vacc_logo ||
                   siteSettings.vacc_logo ||
                   siteSettings['vacc.logo'] ||
                   (isVaccMember ? '/static/images/vacc-logo.png' : null);

  return {
    vacc_member: isVaccMember,
    aaaa_member: businessInfo.aaaa_member || siteSettings.aaaa_member || false,
    abn: businessInfo.abn || siteSettings.abn || '',
    acn: businessInfo.acn || siteSettings.acn || '',
    // Logo, link and text for display
    logo: vaccLogo,
    link: businessInfo.vacc_link || siteSettings.vacc_link || siteSettings['vacc.link'] || 'https://vacc.com.au',
    text: businessInfo.vacc_text || siteSettings.vacc_text || siteSettings['vacc.text'] || '',
  };
};

/**
 * Format phone number for display
 * @param {string} phone - Raw phone number
 * @returns {string} Formatted phone number
 */
export const formatPhone = (phone) => {
  if (!phone) return '';
  // Remove all non-digits
  const digits = phone.replace(/\D/g, '');

  // Format Australian mobile (04xx xxx xxx)
  if (digits.startsWith('04') && digits.length === 10) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }

  // Format Australian landline (0x xxxx xxxx)
  if (digits.startsWith('0') && digits.length === 10) {
    return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`;
  }

  return phone;
};

/**
 * Format phone for tel: link
 * @param {string} phone - Raw phone number
 * @returns {string} Phone number for tel: href
 */
export const phoneHref = (phone) => {
  if (!phone) return '';
  return `tel:${phone.replace(/\s/g, '')}`;
};

export default {
  normalizePhoneNumbers,
  getAddressLines,
  getContactEmail,
  getAccreditationInfo,
  formatPhone,
  phoneHref,
};
